import { minPlanFor } from '@dale/plans';

import type { SignupInput } from '../../api/endpoints/signup';
import type { Category, Neighborhood } from '../../api/schemas';
import {
  isZonedCountry,
  normalizePhone,
  phoneErrorFor,
  readCountryChoice,
  type CountryCode,
  type DeviceHints,
  type SignupCountry,
} from '../../lib/country';
import { CategorySchema } from '../../api/schemas';
import { effectiveCycle, isPlanTier, PLAN_ORDER, readPlanChoice, type BillingCycle, type PlanTier } from '../../lib/plan';
import { readInvitePrefill } from './invite-prefill';

/**
 * Everything the signup wizard asks, one question per screen. Pure and
 * immutable: every change returns a new draft, and the request the wizard
 * sends is the same one the old one-page form sent.
 */

export const MIN_PASSWORD_LENGTH = 12;
/** Mirrors MAX_WORLD_AREA in services/api/src/business-country.ts. */
export const MAX_AREA_LENGTH = 80;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DAY_NAMES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const DIAL_CODE: Readonly<Record<CountryCode, string>> = { PA: '+507', VE: '+58' };

export type ImportHour = { dayOfWeek: number; opensAt: string; closesAt: string; isClosed: boolean };
export type Pin = { lat: number; lng: number };

/**
 * Two signups that never mix: a business (a local, a team) and an independent
 * (one person working on their own). Since 1 Nov 2026 both receive reservations
 * only, with no plan to pick and no card (Martín); a business that arrives with
 * a paid plan in the link (?plan=pro, from /precios) still picks and pays it.
 */
export type SignupMode = 'negocio' | 'independiente';

/** The reservations-only plan: every independent, and every business that did not pick a paid plan. */
export const INDEPENDENT_PLAN: PlanTier = 'reservas';

/** The trades one person runs alone. Restaurants, training centers and the like need a team. */
const INDEPENDENT_CATEGORIES: readonly Category[] = [
  'barbershop',
  'salon',
  'nailstudio',
  'spa',
  'classstudio',
  'gym',
  'tutor',
  'dogwalker',
  'cleaner',
  'plumber',
  'electrician',
  'handyman',
  'doctor',
];

export function categoriesFor(mode: SignupMode): readonly Category[] {
  return mode === 'independiente' ? INDEPENDENT_CATEGORIES : CategorySchema.options;
}

export function planChoicesFor(mode: SignupMode): readonly PlanTier[] {
  return mode === 'independiente' ? [INDEPENDENT_PLAN] : PLAN_ORDER.filter((plan) => plan !== INDEPENDENT_PLAN);
}

export type SignupDraft = {
  mode: SignupMode;
  businessName: string;
  category: Category | null;
  country: SignupCountry;
  /** A Dale zone in Panamá and Venezuela; anywhere else the business's own city or area, as typed. */
  neighborhood: Neighborhood | string | null;
  address: string;
  pin: Pin | null;
  phone: string;
  ownerName: string;
  email: string;
  password: string;
  hours: readonly ImportHour[];
  plan: PlanTier;
  cycle: BillingCycle;
};

export type StepId =
  | 'nombre'
  | 'tipo'
  | 'zona'
  | 'lugar'
  | 'telefono'
  | 'dueno'
  | 'correo'
  | 'clave'
  | 'horario'
  | 'plan';

const BASE_STEPS: readonly StepId[] = ['nombre', 'tipo', 'zona', 'lugar', 'telefono', 'dueno', 'correo', 'clave'];

/** The plan the link picks on purpose (?plan=pro), or undefined when it picks none. */
function pickedPlan(search: string): PlanTier | undefined {
  const plan = new URLSearchParams(search).get('plan')?.trim().toLowerCase() ?? '';
  return isPlanTier(plan) ? plan : undefined;
}

/** A link to Independientes opens the independent signup unless the page already says which one it is. */
export function initialDraft(search: string, mode?: SignupMode, device?: DeviceHints): SignupDraft {
  const choice = readPlanChoice(search);
  const picked = pickedPlan(search);
  const resolved: SignupMode = mode ?? (picked === INDEPENDENT_PLAN ? 'independiente' : 'negocio');
  const { plan, cycle } = planFor(resolved, picked, choice.cycle);
  return {
    mode: resolved,
    businessName: '',
    category: null,
    country: readCountryChoice(search, device),
    neighborhood: null,
    address: '',
    pin: null,
    phone: '',
    ownerName: '',
    email: '',
    password: '',
    hours: [],
    plan,
    cycle,
    // An invite link (?negocio=&correo=&tel=) fills in what Dale already knows.
    ...readInvitePrefill(search),
  };
}

/** Reservations only (monthly, nothing up front) unless a business picked a paid plan. */
function planFor(mode: SignupMode, plan: PlanTier | undefined, cycle: BillingCycle): { plan: PlanTier; cycle: BillingCycle } {
  if (mode === 'independiente' || plan === undefined || plan === INDEPENDENT_PLAN) return { plan: INDEPENDENT_PLAN, cycle: 'monthly' };
  return { plan, cycle };
}

/**
 * Reservations only is for every trade but classes (Martín, 2 Nov 2026): class studios, gyms and
 * training centers start on a paid plan, independents who teach or train included. The API refuses
 * the other way round too.
 */
const PAID_PLAN_CATEGORIES: readonly Category[] = ['classstudio', 'gym', 'trainingcenter'];

export function needsPaidPlan(category: Category | null): boolean {
  return category !== null && PAID_PLAN_CATEGORIES.includes(category);
}

/** True for a signup that only receives reservations: no plan to pick and no card. */
export function reservationsOnly(draft: SignupDraft): boolean {
  if (needsPaidPlan(draft.category)) return false;
  return draft.mode === 'independiente' || draft.plan === INDEPENDENT_PLAN;
}

/** The plan the signup creates: reservations only, or the paid plan picked (Base until one is). */
export function chosenPlan(draft: SignupDraft): PlanTier {
  if (reservationsOnly(draft)) return INDEPENDENT_PLAN;
  if (draft.plan === INDEPENDENT_PLAN) return minPlanFor('clases');
  return draft.plan;
}

/** A new draft with the patch applied. A new country clears the zone and pin it no longer owns. */
export function updateDraft(draft: SignupDraft, patch: Partial<SignupDraft>): SignupDraft {
  const next = { ...draft, ...patch };
  if (patch.country === undefined || patch.country === draft.country) return next;
  return { ...next, neighborhood: null, pin: null };
}

/** The hours step appears only when an import brought hours to review. */
export function stepsFor(draft: SignupDraft): StepId[] {
  return draft.hours.length > 0 ? [...BASE_STEPS, 'horario', 'plan'] : [...BASE_STEPS, 'plan'];
}

/** What keeps a step from moving on, in the words the person reads. Undefined means it can move on. */
export function stepError(step: StepId, draft: SignupDraft): string | undefined {
  switch (step) {
    case 'nombre':
      return draft.businessName.trim() ? undefined : 'Escribe el nombre de tu negocio.';
    case 'tipo':
      if (draft.mode === 'independiente') {
        return draft.category && categoriesFor('independiente').includes(draft.category) ? undefined : 'Elige a qué te dedicas.';
      }
      return draft.category ? undefined : 'Elige qué tipo de negocio es.';
    case 'zona':
      if (!isZonedCountry(draft.country)) {
        const area = draft.neighborhood?.trim() ?? '';
        return area && area.length <= MAX_AREA_LENGTH ? undefined : 'Escribe la ciudad o zona de tu negocio.';
      }
      return draft.neighborhood ? undefined : 'Elige la zona donde está.';
    case 'lugar':
      // Clients find the business by its point on the map, so the point is required; the street line is not.
      return draft.pin ? undefined : 'Marca en el mapa dónde está tu negocio.';
    case 'telefono':
      return phoneError(draft);
    case 'dueno':
      return draft.ownerName.trim() ? undefined : 'Escribe tu nombre.';
    case 'correo':
      return EMAIL_SHAPE.test(draft.email.trim()) ? undefined : 'Revisa el correo, parece que le falta algo.';
    case 'clave':
      return draft.password.length >= MIN_PASSWORD_LENGTH ? undefined : `Usa al menos ${MIN_PASSWORD_LENGTH} caracteres.`;
    case 'horario':
      return hoursError(draft.hours);
    default:
      return undefined;
  }
}

function phoneError(draft: SignupDraft): string | undefined {
  const phone = phoneE164(draft.country, draft.phone);
  if (!phone) return undefined;
  return phoneErrorFor(draft.country, phone);
}

/**
 * People write the number the way they say it ("6123-4567", "0412 1234567"),
 * so the country code is added here when it is missing.
 */
export function phoneE164(country: SignupCountry, raw: string): string {
  const normalized = normalizePhone(raw);
  if (!normalized || normalized.startsWith('+')) return normalized;
  // Abroad the number must carry its own + code: the CRM does not guess another country's prefix.
  if (!isZonedCountry(country)) return normalized;
  return `${DIAL_CODE[country]}${normalized.replace(/^0+/, '')}`;
}

function hoursError(hours: readonly ImportHour[]): string | undefined {
  const backwards = hours.find((day) => !day.isClosed && day.opensAt >= day.closesAt);
  if (!backwards) return undefined;
  return `Revisa el horario: el ${DAY_NAMES[backwards.dayOfWeek]} cierra antes de abrir.`;
}

/** The atomic signup request: required fields always, the optional ones only when given. */
export function signupInputFrom(draft: SignupDraft): SignupInput {
  const businessName = draft.businessName.trim();
  const phone = phoneE164(draft.country, draft.phone);
  const address = draft.address.trim();
  const openDays = draft.hours
    .filter((day) => !day.isClosed)
    .map(({ dayOfWeek, opensAt, closesAt }) => ({ dayOfWeek, opensAt, closesAt }));
  const plan = chosenPlan(draft);
  const cycle = plan === INDEPENDENT_PLAN ? 'monthly' : draft.cycle;
  return {
    // One local is the common case, so the organización takes the business's
    // own name; more locales are added later from the CRM.
    organizationName: businessName,
    businessName,
    category: draft.category as Category,
    neighborhood: (draft.neighborhood ?? '').trim(),
    country: draft.country,
    // Panamá and Venezuela keep their own clock; anywhere else the API schedules with the device's.
    ...(isZonedCountry(draft.country) ? {} : { timeZone: deviceTimeZone() }),
    ownerName: draft.ownerName.trim(),
    email: draft.email.trim(),
    password: draft.password,
    plan,
    billingCycle: effectiveCycle(plan, cycle),
    ...(address ? { address } : {}),
    ...(phone ? { contactPhoneE164: phone } : {}),
    ...(draft.pin ? { lat: draft.pin.lat, lng: draft.pin.lng } : {}),
    ...(draft.hours.length ? { hours: openDays } : {}),
  };
}

/** The device clock (IANA), or UTC when the browser cannot say. */
function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? '';
}
